import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import ConfirmModal from './ConfirmModal';
import AdminApplications from './AdminApplications';
import AdminPlatformAnalytics from './AdminPlatformAnalytics';
import AdminRestaurantDirectory from './AdminRestaurantDirectory';
import AdminBilling from './AdminBilling';

export default function AdminPanel({ onLogout }) {
  const [activeTab, setActiveTab]     = useState('restaurants');
  const [pendingApps, setPendingApps] = useState(0);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [form, setForm]               = useState({ name: '', username: '', password: '', email: '' });
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [selected, setSelected]       = useState(null); // restaurant detail modal
  const [pwdEdit, setPwdEdit]         = useState(null); // { id, password }
  const [emailEdit, setEmailEdit]     = useState(null); // { id, name, email }
  const [showPwd, setShowPwd]         = useState(false);
  const [widgetStyle, setWidgetStyle] = useState('bubble'); // 'bubble' | 'button'
  const [confirm, setConfirm]         = useState(null);

  const load = useCallback(async () => {
    try { setRestaurants(await api.adminGetRestaurants()); }
    catch (_) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.adminGetPlatformAnalytics().then(d => setPendingApps(d.pendingApps || 0)).catch(() => {});
  }, []);

  const create = async e => {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    try {
      await api.adminCreateRestaurant(form);
      setSuccess(`Account created for "${form.name}"`);
      setForm({ name: '', username: '', password: '', email: '' });
      load();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const remove = (id, name) => {
    setConfirm({
      message: `Delete "${name}"?`,
      detail: 'This will permanently delete the restaurant and all their data. This cannot be undone.',
      onConfirm: async () => {
        setConfirm(null);
        try { await api.adminDeleteRestaurant(id); load(); }
        catch (err) { setError(err.message); }
      },
    });
  };

  const toggleActive = async (id, currentlyActive) => {
    try { await api.adminSetStatus(id, !currentlyActive); load(); setSelected(null); }
    catch (err) { setError(err.message); }
  };

  const savePassword = async () => {
    if (!pwdEdit?.password) return;
    try {
      await api.adminSetPassword(pwdEdit.id, pwdEdit.password);
      setPwdEdit(null);
      setSuccess('Password updated');
      load();
      setSelected(prev => prev?.id === pwdEdit.id ? { ...prev, plain_password: pwdEdit.password } : prev);
    } catch (err) { setError(err.message); }
  };

  const saveEmail = async () => {
    try {
      await api.adminSetEmail(emailEdit.id, emailEdit.email);
      setEmailEdit(null);
      setSuccess('Email updated');
      load();
      setSelected(prev => prev?.id === emailEdit.id ? { ...prev, email: emailEdit.email } : prev);
    } catch (err) { setError(err.message); }
  };

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.brand}>
          <div style={s.brandMark}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 2h18M3 22h18M12 2v20M3 12h18" />
            </svg>
          </div>
          <div>
            <div style={s.brandName}>Seva</div>
            <div style={s.brandSub}>Admin</div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[
            { key: 'restaurants', label: 'Restaurants' },
            { key: 'applications', label: 'Applications', badge: pendingApps },
            { key: 'analytics',   label: 'Analytics' },
            { key: 'directory',   label: 'Directory' },
            { key: 'billing',     label: 'Billing' },
          ].map(item => (
            <button key={item.key} onClick={() => setActiveTab(item.key)}
              style={{ ...s.navBtn, ...(activeTab === item.key ? s.navBtnActive : {}) }}>
              <span>{item.label}</span>
              {item.badge > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <button onClick={onLogout} style={s.logoutBtn}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          </svg>
          <span>Sign out</span>
        </button>
      </div>

      <main style={s.main}>
        <div style={s.content}>

        {activeTab === 'applications' && <AdminApplications />}
        {activeTab === 'analytics'    && <AdminPlatformAnalytics />}
        {activeTab === 'directory'    && <AdminRestaurantDirectory restaurants={restaurants} />}
        {activeTab === 'billing'      && <AdminBilling />}

        {activeTab === 'restaurants' && (<>
          <h1 style={s.h1}>Restaurants</h1>
          <p style={s.sub}>Manage restaurant accounts. Each restaurant gets their own login, data, and AI assistant.</p>

          {error   && <div style={s.errBanner}>{error}</div>}
          {success && <div style={s.okBanner}>{success}</div>}

          {/* Create form */}
          <div style={s.card}>
            <div style={s.cardTitle}>Create new restaurant account</div>
            <form onSubmit={create} style={s.form}>
              <div style={s.row}>
                <div style={s.field}>
                  <label style={s.label}>Restaurant name</label>
                  <input style={s.input} type="text" placeholder="e.g. The Golden Fork" required
                    value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Login username</label>
                  <input style={s.input} type="text" placeholder="e.g. goldenfork" required
                    value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Password</label>
                  <input style={s.input} type="password" placeholder="Choose a secure password" required
                    value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Email <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#b0bac5' }}>(optional)</span></label>
                  <input style={s.input} type="email" placeholder="contact@restaurant.com"
                    value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <button type="submit" style={s.createBtn} disabled={saving}>
                {saving ? 'Creating…' : '+ Create account'}
              </button>
            </form>
          </div>

          {/* Restaurant list */}
          <div style={s.card}>
            <div style={s.cardTitle}>All accounts ({restaurants.length})</div>
            {loading ? (
              <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</div>
            ) : restaurants.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 14 }}>No restaurants yet. Create one above.</div>
            ) : (
              <div style={s.table}>
                <div style={s.thead}>
                  {['Restaurant', 'Username', 'Created', 'Actions'].map(h =>
                    <div key={h} style={s.th}>{h}</div>
                  )}
                </div>
                {restaurants.map(r => (
                  <div key={r.id} style={{ ...s.trow, opacity: r.is_active === 0 ? 0.6 : 1 }} onClick={() => { setSelected(r); setShowPwd(false); }}>
                    <div style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={s.rName}>{r.name}</div>
                        {r.is_active === 0 && (
                          <span style={s.inactiveBadge}>Inactive</span>
                        )}
                      </div>
                      <div style={s.rId}>ID #{r.id}</div>
                    </div>
                    <div style={{ ...s.td, fontFamily: 'monospace', fontSize: 13 }}>{r.username}</div>
                    <div style={s.td}>
                      {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div style={{ ...s.td, display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                      <button style={s.actionBtn} onClick={() => setPwdEdit({ id: r.id, name: r.name, password: '' })}>
                        Change password
                      </button>
                      <button
                        style={{ ...s.actionBtn, ...(r.is_active === 0 ? s.activateBtn : s.deactivateBtn) }}
                        onClick={() => toggleActive(r.id, r.is_active !== 0)}
                      >
                        {r.is_active === 0 ? 'Activate' : 'Deactivate'}
                      </button>
                      <button style={{ ...s.actionBtn, ...s.deleteBtn }} onClick={() => remove(r.id, r.name)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Widget info card */}
          <div style={s.card}>
            <div style={s.cardTitle}>Chatbot widget</div>
            <p style={s.hint}>Embed the AI chatbot on any restaurant's website. Choose a trigger style, then copy the snippet.</p>

            {/* Style toggle */}
            <div style={s.widgetToggle}>
              <button
                style={{ ...s.widgetToggleBtn, ...(widgetStyle === 'bubble' ? s.widgetToggleActive : {}) }}
                onClick={() => setWidgetStyle('bubble')}
              >
                Chat bubble
              </button>
              <button
                style={{ ...s.widgetToggleBtn, ...(widgetStyle === 'button' ? s.widgetToggleActive : {}) }}
                onClick={() => setWidgetStyle('button')}
              >
                "Make a Reservation" button
              </button>
            </div>

            <p style={{ ...s.hint, marginBottom: 10 }}>Click a restaurant row above to see its personalised embed code.</p>
          </div>
        </>)}
        </div>
      </main>

      {/* Restaurant detail modal */}
      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.detailHeader}>
              <div>
                <h2 style={s.modalTitle}>{selected.name}</h2>
                <div style={s.detailSub}>ID #{selected.id}</div>
              </div>
              <button style={s.xBtn} onClick={() => setSelected(null)}>×</button>
            </div>
            <div style={s.detailDivider} />

            <div style={s.detailGrid}>
              <div style={s.detailRow}>
                <span style={s.detailKey}>Username</span>
                <span style={s.detailVal}>{selected.username}</span>
              </div>
              <div style={s.detailRow}>
                <span style={s.detailKey}>Password</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...s.detailVal, fontFamily: 'monospace', letterSpacing: showPwd ? 0 : 2 }}>
                    {showPwd
                      ? (selected.plain_password || '—')
                      : (selected.plain_password ? '••••••••' : 'Not stored')}
                  </span>
                  {selected.plain_password && (
                    <button style={s.showBtn} onClick={() => setShowPwd(p => !p)}>
                      {showPwd ? 'Hide' : 'Show'}
                    </button>
                  )}
                </div>
              </div>
              <div style={s.detailRow}>
                <span style={s.detailKey}>Email</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={s.detailVal}>{selected.email || <span style={{ color: '#94a3b8' }}>—</span>}</span>
                  <button style={s.showBtn} onClick={() => setEmailEdit({ id: selected.id, name: selected.name, email: selected.email || '' })}>
                    Edit
                  </button>
                </div>
              </div>
              <div style={s.detailRow}>
                <span style={s.detailKey}>Created</span>
                <span style={s.detailVal}>
                  {new Date(selected.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div style={s.detailRow}>
                <span style={s.detailKey}>Widget embed</span>
                <div style={{ flex: 1 }}>
                  <div style={s.embedCode}>
                    {`<script src="${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/widget.js" data-id="${selected.widget_token || '—'}"${widgetStyle === 'button' ? ' data-trigger="button"' : ''} defer></script>`}
                  </div>
                  <button style={{ ...s.showBtn, marginTop: 6 }} onClick={() => {
                    navigator.clipboard.writeText(`<script src="${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/widget.js" data-id="${selected.widget_token}"${widgetStyle === 'button' ? ' data-trigger="button"' : ''} defer></script>`);
                  }}>Copy</button>
                </div>
              </div>
            </div>

            <div style={s.detailDivider} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={s.actionBtn} onClick={() => { setSelected(null); setPwdEdit({ id: selected.id, name: selected.name, password: '' }); }}>
                Change password
              </button>
              <button
                style={{ ...s.actionBtn, ...(selected.is_active === 0 ? s.activateBtn : s.deactivateBtn) }}
                onClick={() => toggleActive(selected.id, selected.is_active !== 0)}
              >
                {selected.is_active === 0 ? 'Activate account' : 'Deactivate account'}
              </button>
              <button style={{ ...s.actionBtn, ...s.deleteBtn }} onClick={() => { setSelected(null); remove(selected.id, selected.name); }}>
                Delete account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change password modal */}
      {pwdEdit && (
        <div style={s.overlay} onClick={() => setPwdEdit(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitle}>Change password — {pwdEdit.name}</h2>
            <div style={s.field}>
              <label style={s.label}>New password</label>
              <input style={s.input} type="password" autoFocus
                value={pwdEdit.password}
                onChange={e => setPwdEdit(p => ({ ...p, password: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button style={s.createBtn} onClick={savePassword}>Save</button>
              <button style={s.cancelBtn} onClick={() => setPwdEdit(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit email modal */}
      {emailEdit && (
        <div style={s.overlay} onClick={() => setEmailEdit(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitle}>Update email — {emailEdit.name}</h2>
            <div style={{ ...s.field, marginTop: 16 }}>
              <label style={s.label}>Email address</label>
              <input style={s.input} type="email" autoFocus placeholder="contact@restaurant.com"
                value={emailEdit.email}
                onChange={e => setEmailEdit(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button style={s.createBtn} onClick={saveEmail}>Save</button>
              <button style={s.cancelBtn} onClick={() => setEmailEdit(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          message={confirm.message}
          detail={confirm.detail}
          confirmLabel="Delete"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

const s = {
  page:      { display: 'flex', height: '100vh', overflow: 'hidden' },
  sidebar:   { width: 220, flexShrink: 0, background: '#0f172a', display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.04)' },
  brand:     { display: 'flex', alignItems: 'center', gap: 10, padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  brandMark: { width: 34, height: 34, background: '#4f46e5', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  brandName: { fontSize: 14, fontWeight: 700, color: '#f1f5f9', letterSpacing: '-0.2px' },
  brandSub:  { fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 1 },
  navBtn:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'none', border: 'none', borderRadius: 8, color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left', width: '100%' },
  navBtnActive:{ background: 'rgba(255,255,255,0.08)', color: '#f1f5f9', fontWeight: 600 },
  logoutBtn:  { display: 'flex', alignItems: 'center', gap: 9, margin: '0 10px 16px', padding: '8px 10px', background: 'none', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 7, color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 13 },

  main:    { flex: 1, overflow: 'auto', background: '#f8fafc' },
  content: { padding: '32px 36px', maxWidth: 960, margin: '0 auto' },
  h1:      { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:     { fontSize: 14, color: '#64748b', marginTop: 4, marginBottom: 26 },

  errBanner: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 16 },
  okBanner:  { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 16 },

  card:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, padding: '22px 26px', marginBottom: 16, boxShadow: '0 1px 4px rgba(15,23,42,0.06)' },
  cardTitle: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18 },
  form:      { display: 'flex', flexDirection: 'column', gap: 0 },
  row:       { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 },
  field:     { flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 6 },
  label:     { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input:     { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 13px', fontSize: 14, outline: 'none', color: '#0f172a' },
  createBtn: { background: '#4f46e5', border: 'none', borderRadius: 10, padding: '11px 24px', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', alignSelf: 'flex-start' },
  cancelBtn: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 24px', fontSize: 14, color: '#475569', cursor: 'pointer' },

  table:  { display: 'flex', flexDirection: 'column' },
  thead:  { display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 2fr', gap: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 4 },
  th:     { fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' },
  trow:   { display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 2fr', gap: 12, padding: '14px 12px', borderBottom: '1px solid #f1f5f9', alignItems: 'center' },
  td:     { fontSize: 13, color: '#475569' },
  rName:  { fontWeight: 700, color: '#0f172a', fontSize: 14 },
  rId:    { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  actionBtn:     { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: '#334155', fontWeight: 500 },
  deleteBtn:     { background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' },
  deactivateBtn: { background: '#fffbeb', borderColor: '#fde68a', color: '#b45309' },
  activateBtn:   { background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' },
  inactiveBadge: { fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 5, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  hint:    { fontSize: 13, color: '#64748b', margin: '0 0 10px' },
  codeBlock: { background: '#0f172a', color: '#e2e8f0', borderRadius: 10, padding: '14px 18px', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre', overflowX: 'auto' },
  code:    { background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', color: '#4f46e5' },

  widgetToggle:       { display: 'flex', gap: 6, marginBottom: 14, background: '#f1f5f9', borderRadius: 10, padding: 4 },
  widgetToggleBtn:    { flex: 1, padding: '7px 14px', borderRadius: 7, border: 'none', background: 'none', fontSize: 13, color: '#64748b', cursor: 'pointer', fontWeight: 500 },
  widgetToggleActive: { background: '#fff', color: '#0f172a', fontWeight: 700, boxShadow: '0 1px 4px rgba(15,23,42,0.08)' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 },
  modal:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, padding: '28px 32px', width: 440, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(15,23,42,0.18)' },
  modalTitle: { fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 },

  detailHeader:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 },
  detailSub:     { fontSize: 12, color: '#94a3b8', marginTop: 4 },
  xBtn:          { background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', lineHeight: 1, padding: '0 2px' },
  detailDivider: { height: 1, background: '#f1f5f9', margin: '0 0 20px' },
  detailGrid:    { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 },
  detailRow:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
  detailKey:     { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 },
  detailVal:     { fontSize: 14, color: '#0f172a', textAlign: 'right' },
  embedCode:     { fontFamily: 'monospace', fontSize: 11, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, padding: '8px 10px', color: '#334155', wordBreak: 'break-all', lineHeight: 1.5 },
  showBtn:       { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 11, color: '#475569', cursor: 'pointer', fontWeight: 600, flexShrink: 0 },
};
